import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";

import theme from "./theme";
import NavBar from "./components/NavBar";
import { isAdmin, getToken } from "./auth/token";
import BACKEND_URL from "./config";

// Reuse same auth header helper
function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function OrganizationsAdmin() {
  const navigate = useNavigate();
  const admin = isAdmin();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [orgs, setOrgs] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [products, setProducts] = useState([]);
  const [interviewTypes, setInterviewTypes] = useState([]); // NEW: interview types with counts
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    notes: "",
    is_active: true,
    industry_subscription_ids: [],
    product_subscription_ids: [],
  });

  const [createForm, setCreateForm] = useState({
    name: "",
    notes: "",
    is_active: true,
  });

  // Redirect non-admins
  useEffect(() => {
    if (!admin) {
      navigate("/");
    }
  }, [admin, navigate]);

  async function fetchOrgs() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/organizations`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setOrgs(data);
    } catch (err) {
      console.error("Error fetching organizations", err);
      setError("Failed to load organizations.");
    } finally {
      setLoading(false);
    }
  }

  async function fetchIndustries() {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/industries`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setIndustries(data);
    } catch (err) {
      console.error("Error fetching industries", err);
    }
  }

  async function fetchProducts() {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/products`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      console.error("Error fetching products", err);
    }
  }

  // NEW: fetch interview types (with submitted/approved counts)
  async function fetchInterviewTypes() {
    try {
      const res = await fetch(`${BACKEND_URL}/api/interview-types`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setInterviewTypes(data || []);
    } catch (err) {
      console.error("Error fetching interview types", err);
    }
  }

  useEffect(() => {
    if (admin) {
      fetchOrgs();
      fetchIndustries();
      fetchProducts();
      fetchInterviewTypes(); // NEW
    }
  }, [admin]);

  function handleEditClick(org) {
    setEditingOrgId(org.id);
    setEditForm({
      name: org.name || "",
      notes: org.notes || "",
      is_active: org.is_active !== false,
      industry_subscription_ids: org.industry_subscription_ids || [],
      product_subscription_ids: org.product_subscription_ids || [],
    });
  }

  function handleEditFormChange(e) {
    const { name, value, type, checked, options } = e.target;

    if (name === "industry_subscription_ids" || name === "product_subscription_ids") {
      const selected = Array.from(options)
        .filter((opt) => opt.selected)
        .map((opt) => opt.value);
      setEditForm((prev) => ({ ...prev, [name]: selected }));
      return;
    }

    setEditForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!editingOrgId) return;
    setError("");

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/organizations/${editingOrgId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({
          name: editForm.name.trim(),
          notes: editForm.notes,
          is_active: !!editForm.is_active,
          industry_subscription_ids: editForm.industry_subscription_ids || [],
          product_subscription_ids: editForm.product_subscription_ids || [],
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      await fetchOrgs();
      setEditingOrgId(null);
    } catch (err) {
      console.error("Error saving organization", err);
      setError(err.message || "Failed to save organization.");
    }
  }

  // NEW: handlers for create-org form
  function handleCreateFormChange(e) {
    const { name, value, type, checked } = e.target;
    setCreateForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleCreateOrg(e) {
    e.preventDefault();
    setError("");

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/organizations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({
          name: createForm.name.trim(),
          notes: createForm.notes,
          is_active: !!createForm.is_active,
          // Start with no subscriptions; admin can edit them after creation
          industry_subscription_ids: [],
          product_subscription_ids: [],
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      await fetchOrgs();

      // Reset form
      setCreateForm({
        name: "",
        notes: "",
        is_active: true,
      });
    } catch (err) {
      console.error("Error creating organization", err);
      setError(err.message || "Failed to create organization.");
    }
  }

  // Login-as for a specific user email in an org
  async function handleLoginAsUser(email) {
    setError("");

    const currentToken = getToken();
    if (!currentToken) {
      setError("No admin token found; please log in again.");
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login-as`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      const token = data.access_token;
      if (!token) {
        throw new Error("No access_token returned from login-as.");
      }

      // Save the current admin token so we can come back later
      localStorage.setItem("admin_access_token_backup", currentToken);
      // Replace with impersonated token
      localStorage.setItem("access_token", token);

      // Navigate as the impersonated user
      window.location.href = "/";
    } catch (err) {
      console.error("Error logging in as user", err);
      setError(err.message || "Failed to log in as user.");
    }
  }

  // Show a "Return to admin account" button if we have a backup token
  const [hasAdminBackup, setHasAdminBackup] = useState(
    !!localStorage.getItem("admin_access_token_backup")
  );

  function handleReturnToAdmin() {
    const backup = localStorage.getItem("admin_access_token_backup");
    if (!backup) {
      setHasAdminBackup(false);
      return;
    }
    localStorage.setItem("access_token", backup);
    localStorage.removeItem("admin_access_token_backup");
    // Reload as the original admin
    window.location.href = "/admin/organizations";
  }

  const industryById = useMemo(() => {
    const map = {};
    (industries || []).forEach((ind) => {
      if (ind && ind.id) {
        map[ind.id] = ind;
      }
    });
    return map;
  }, [industries]);

  const productById = useMemo(() => {
    const map = {};
    (products || []).forEach((prod) => {
      if (prod && prod.id) {
        map[prod.id] = prod;
      }
    });
    return map;
  }, [products]);

  // NEW: build maps from industry_id/product_id -> interview count (approved preferred)
  const industryInterviewCounts = useMemo(() => {
    const counts = {};
    (interviewTypes || []).forEach((t) => {
      const approved = t.interviews_approved_count ?? t.interviews_submitted_count ?? 0;
      const ids = t.industry_ids || [];
      ids.forEach((id) => {
        counts[id] = (counts[id] || 0) + approved;
      });
    });
    return counts;
  }, [interviewTypes]);

  const productInterviewCounts = useMemo(() => {
    const counts = {};
    (interviewTypes || []).forEach((t) => {
      const approved = t.interviews_approved_count ?? t.interviews_submitted_count ?? 0;
      const ids = t.product_ids || [];
      ids.forEach((id) => {
        counts[id] = (counts[id] || 0) + approved;
      });
    });
    return counts;
  }, [interviewTypes]);

  const sortedOrgs = useMemo(() => {
    return [...orgs].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [orgs]);

  return (
    <ThemeProvider theme={theme}>
      <NavBar />
      <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        <Box
          sx={{
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="h4">
            Organizations & Subscriptions
          </Typography>
          {hasAdminBackup && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleReturnToAdmin}
            >
              Return to admin account
            </Button>
          )}
        </Box>
        <Typography variant="h8" sx={{ mb: 2 }}>
          <strong>How to create a dataset (set of interviews) and give a user a subscription to it:</strong>
          <br></br>
          <br></br> <strong>Create the batch of interviews:</strong>
          <br></br>1. Create an interview_type (in /admin/interview-types), e.g. “PocketPennies in the FinTech Industry” and assign it to one or more industries (e.g. “Fintech”) and/or products (“PocketPennies”). Choose "Yes" to "Create new user" option, which creates a new user (e.g. research-pocket-change-industries@conversation-intelligence.com), who gets an organization_id assigned to them upon creation.
          <br></br>2. Then, any AI interviews created via that interview_type will produce Customer Story JSONs that have the industry_ids and product_ids associated with their parent interview_type.
          <br></br>
          <br></br><strong>Give a subscription:</strong>
          <br></br>3. To give a user access to those Customer Story JSONs when they login, you subscribe that user’s organization to any one of the industries/products in those Customer Story JSONs (/admin/organizations).
          <br></br>4. When they login, the app’s backend finds all the Customer Story JSONs the user’s organization should have access to and sends them to the frontend.
          <br></br>
          <br></br><strong>Notes:</strong>
          <br></br>- The user who owns the organization that is the owner of an interview type will see the AI interviews in their file processing queue when those interviews are "Reviewed & Approved" but the resulting Customer Story JSONs will not be visible in their analysis dashboard/pages unless you give them a subscription to their own interview type. 
          <br></br>
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* NEW: Create-organization form */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Create new organization
              </Typography>

              <form onSubmit={handleCreateOrg}>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <label>
                    Name:
                    <input
                      type="text"
                      name="name"
                      value={createForm.name}
                      onChange={handleCreateFormChange}
                      style={{ width: "100%", marginTop: 4 }}
                      required
                    />
                  </label>

                  <label>
                    Active?
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={!!createForm.is_active}
                      onChange={handleCreateFormChange}
                      style={{ marginLeft: 8 }}
                    />
                  </label>

                  <label style={{ gridColumn: "1 / -1" }}>
                    Notes:
                    <textarea
                      name="notes"
                      value={createForm.notes}
                      onChange={handleCreateFormChange}
                      rows={3}
                      style={{ width: "100%", marginTop: 4 }}
                    />
                  </label>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Button type="submit" variant="contained">
                    Create organization
                  </Button>
                </Box>
              </form>
            </Paper>

            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Organizations
              </Typography>

              {sortedOrgs.length === 0 ? (
                <Typography>No organizations found.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Org ID</TableCell>
                        <TableCell>User emails</TableCell>
                        <TableCell align="right">Users</TableCell>
                        <TableCell align="right">Self-uploaded interviews</TableCell>
                        <TableCell>Industry subs</TableCell>
                        <TableCell>Product subs</TableCell>
                        <TableCell>Active?</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedOrgs.map((org) => (
                        <TableRow key={org.id}>
                          <TableCell>{org.name}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", wordBreak: "break-all" }}>
                            {org.id}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "0.75rem",
                              maxWidth: 260,
                              whiteSpace: "pre-line",
                              wordBreak: "break-all",
                            }}
                          >
                            {(org.user_emails || []).length === 0 ? (
                              "-"
                            ) : (
                              (org.user_emails || []).map((email) => (
                                <div key={email}>
                                  {email}{" "}
                                  <span
                                    style={{
                                      color: "#1976d2",
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                      marginLeft: 4,
                                    }}
                                    onClick={() => handleLoginAsUser(email)}
                                  >
                                    (login)
                                  </span>
                                </div>
                              ))
                            )}
                          </TableCell>
                          <TableCell align="right">{org.user_count || 0}</TableCell>
                          <TableCell align="right">
                            {org.self_uploaded_interviews_count != null
                              ? org.self_uploaded_interviews_count
                              : 0}
                          </TableCell>

                          {/* Industry subs: show names + dataset interview counts, or "None" */}
                          <TableCell sx={{ fontSize: "0.75rem" }}>
                            {(() => {
                              const ids = org.industry_subscription_ids || [];
                              if (!ids.length) return "None";

                              const labels = ids.map((id) => {
                                const ind = industryById[id];
                                const name = ind ? ind.name : id;
                                const count = industryInterviewCounts[id] ?? 0;
                                return `${name} (${count})`;
                              });

                              return labels.join(", ");
                            })()}
                          </TableCell>

                          {/* Product subs: show names + dataset interview counts, or "None" */}
                          <TableCell sx={{ fontSize: "0.75rem" }}>
                            {(() => {
                              const ids = org.product_subscription_ids || [];
                              if (!ids.length) return "None";

                              const labels = ids.map((id) => {
                                const prod = productById[id];
                                const name = prod ? prod.name : id;
                                const count = productInterviewCounts[id] ?? 0;
                                return `${name} (${count})`;
                              });

                              return labels.join(", ");
                            })()}
                          </TableCell>

                          <TableCell>{org.is_active ? "Yes" : "No"}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleEditClick(org)}
                            >
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {editingOrgId && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Edit organization
                </Typography>

                <form onSubmit={handleSave}>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    }}
                  >
                    <label>
                      Name:
                      <input
                        type="text"
                        name="name"
                        value={editForm.name}
                        onChange={handleEditFormChange}
                        style={{ width: "100%", marginTop: 4 }}
                      />
                    </label>

                    <label>
                      Active?
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={!!editForm.is_active}
                        onChange={handleEditFormChange}
                        style={{ marginLeft: 8 }}
                      />
                    </label>

                    <label style={{ gridColumn: "1 / -1" }}>
                      Notes:
                      <textarea
                        name="notes"
                        value={editForm.notes}
                        onChange={handleEditFormChange}
                        rows={3}
                        style={{ width: "100%", marginTop: 4 }}
                      />
                    </label>

                    <label>
                      Industry subscriptions:
                      <select
                        multiple
                        name="industry_subscription_ids"
                        value={editForm.industry_subscription_ids}
                        onChange={handleEditFormChange}
                        style={{ width: "100%", marginTop: 4, minHeight: 100 }}
                      >
                        {industries.map((ind) => (
                          <option key={ind.id} value={ind.id}>
                            {ind.name} ({ind.slug})
                          </option>
                        ))}
                      </select>
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        Hold Ctrl (Windows) or Command (Mac) to select multiple.
                      </div>
                    </label>

                    <label>
                      Product subscriptions:
                      <select
                        multiple
                        name="product_subscription_ids"
                        value={editForm.product_subscription_ids}
                        onChange={handleEditFormChange}
                        style={{ width: "100%", marginTop: 4, minHeight: 100 }}
                      >
                        {products.map((prod) => (
                          <option key={prod.id} value={prod.id}>
                            {prod.name} ({prod.slug})
                          </option>
                        ))}
                      </select>
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        Link this org to one or more products.
                      </div>
                    </label>
                  </Box>

                  <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                    <Button type="submit" variant="contained">
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="text"
                      onClick={() => setEditingOrgId(null)}
                    >
                      Cancel
                    </Button>
                  </Box>
                </form>
              </Paper>
            )}
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default OrganizationsAdmin;
